import { Col, Modal, Row, Table } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';

import { ETypPrevodu } from 'services/specialniSluzby/uePozadavky';
import { IZaevidovaniGetDLT, ueZaevidovaniGetDLT, ueZaevidovaniPostDLT } from 'services/specialniSluzby/ueZaevidovani';
import { IZruseniGetDLT, ueZruseniDeleteDLT, ueZruseniGetDLT } from 'services/specialniSluzby/ueZruseni';

import routePathEnum from 'enums/routePathEnum';
import { ETypSeznam } from 'enums/specialniSluzby/typSeznam';

import formatInputNumberThousand from 'utils/formatInputNumberThousand';
import getLanguageObject from 'utils/getLanguageObject';

const paginationDefaultVals = {
    offset: 0,
    page: 1,
    limit: 10
};

interface IPagination {
    offset: number;
    page: number;
    limit: number;
}

const SeznamPozadavkuDLT: React.FC = () => {
    const { aktivniFirma, specialniSluzby, language } = useContext(Context);
    const { confirm } = Modal;
    const { navigateWorkaround } = useNavigateWorkaround();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataLze, setDataLze] = useState<IZaevidovaniGetDLT>();
    const [dataNelze, setDataNelze] = useState<IZaevidovaniGetDLT>();
    const [paginationNelze, setPaginationNelze] = useState<IPagination>(paginationDefaultVals);
    const [paginationLze, setPaginationLze] = useState<IPagination>(paginationDefaultVals);
    const [dataZruseni, setDataZruseni] = useState<IZruseniGetDLT>();
    const [pagination, setPagination] = useState<IPagination>(paginationDefaultVals);
    const [modalMessage, setModalMessage] = useState<string>('');

    const loadDataFunc = (lze: boolean = true, offset?: number) => {
        if (
            specialniSluzby?.detail &&
            (specialniSluzby.seznamTyp === ETypSeznam.OVERENY || specialniSluzby.seznamTyp === ETypSeznam.ZAEVIDOVANY)
        ) {
            setLoading(true);
            return ueZaevidovaniGetDLT(specialniSluzby.detail.id!, {
                idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                ucet: specialniSluzby.detailFiltr?.ucet,
                zpusobpor: specialniSluzby.detailFiltr?.zpusobpor,
                stav: specialniSluzby.detailFiltr?.stav,
                evidovat: lze,
                offset: offset !== undefined ? offset : lze ? paginationLze.offset : paginationNelze.offset,
                limit: lze ? paginationLze.limit : paginationNelze.limit
            });
        } else if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.SMAZANY) {
            setLoading(true);
            ueZruseniGetDLT(
                specialniSluzby.detail.id!,
                specialniSluzby.pozadavek
                    ? {
                          idpolozky: specialniSluzby.pozadavek?.idpolozky,
                          ucet: specialniSluzby.pozadavek?.ucet,
                          zpusobpor: specialniSluzby.pozadavek?.zpusobpor,
                          offset: offset !== undefined ? offset : 0,
                          stav: specialniSluzby.pozadavek?.stav,
                          limit: pagination.limit
                      }
                    : {
                          idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                          ucet: specialniSluzby.detailFiltr?.ucet,
                          zpusobpor: specialniSluzby.detailFiltr?.zpusobpor,
                          offset: offset !== undefined ? offset : 0,
                          stav: specialniSluzby.detailFiltr?.stav,
                          limit: pagination.limit
                      }
            )
                .then((resp: IZruseniGetDLT) => {
                    setDataZruseni(resp as IZruseniGetDLT);
                })
                .catch((err: any) => {
                    setErrStatus(err.response?.status);
                })
                .finally(() => {
                    setLoading(false);
                    setErrStatus(0);
                });
        }
    };

    const loadDataBoth = () => {
        if (specialniSluzby?.detail && specialniSluzby.seznamTyp === ETypSeznam.ZAEVIDOVANY) {
            setLoading(true);
            const promisses = [];

            promisses.push(
                new Promise(resolve => {
                    const resProm = loadDataFunc(true);
                    if (resProm)
                        resProm
                            .then((resp: IZaevidovaniGetDLT) => {
                                setDataLze(resp);
                            })
                            .catch((err: any) => {
                                setErrStatus(err.response?.status);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            promisses.push(
                new Promise(resolve => {
                    const resProm = loadDataFunc(false);
                    if (resProm)
                        resProm
                            .then((resp: IZaevidovaniGetDLT) => {
                                setDataNelze(resp);
                            })
                            .catch((err: any) => {
                                setErrStatus(err.response?.status);
                            })
                            .finally(() => {
                                resolve(true);
                            });
                })
            );

            Promise.all(promisses).then(() => {
                setLoading(false);
                setErrStatus(0);
            });
        }
    };

    const columns = [
        {
            title: <TranslateText>ISIN</TranslateText>,
            dataIndex: 'isin',
            key: 'isin',
            render: () => specialniSluzby?.detail?.isin
        },
        {
            title: <TranslateText>UCET</TranslateText>,
            dataIndex: 'ucet',
            key: 'ucet'
        },
        {
            title: <TranslateText>POCET_CP</TranslateText>,
            dataIndex: 'pocetcp',
            key: 'pocetcp',
            render: (val: string) => formatInputNumberThousand(val || 0)
        },
        ...(specialniSluzby?.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
            ? [
                  {
                      title: <TranslateText>CASTKA</TranslateText>,
                      dataIndex: 'castka',
                      key: 'castka',
                      render: (val: string) => formatInputNumberThousand(val || 0)
                  }
              ]
            : []),
        {
            title: <TranslateText>CISLO_ZASTAVNI_SMLOUVY</TranslateText>,
            dataIndex: 'cislozastavnismlouvy',
            key: 'cislozastavnismlouvy'
        },
        {
            title: <TranslateText>ZPUSOB_PORIZENI</TranslateText>,
            dataIndex: 'zpusobporizeni',
            key: 'zpusobporizeni',
            render: (text: string) => {
                return <TranslateText>{'SS_DETAIL_TAB_PORIZENI_' + text}</TranslateText>;
            }
        },
        {
            title: <TranslateText>UPOZORNENI</TranslateText>,
            dataIndex: 'upozorneni',
            key: 'upozorneni'
        }
    ];

    useEffect(() => {
        if (specialniSluzby) {
            if (specialniSluzby.seznamTyp === ETypSeznam.SMAZANY) {
                specialniSluzby?.detail?.id && loadDataFunc();
            } else {
                specialniSluzby?.detail?.id && loadDataBoth();
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [specialniSluzby?.detail?.id]);

    const renderModal = (msg: string) => {
        setModalMessage(msg);
    };

    const renderButtons = () => {
        return (
            <Row className="button-row">
                <ButtonComponent
                    id="seznamPozadavkuZpetBtn"
                    type="default"
                    buttontext="ZPET"
                    onClick={() => {
                        navigateWorkaround(
                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT}`
                        );
                    }}
                />
                {specialniSluzby && (
                    <ButtonComponent
                        id="seznamPozadavkuSubmitBtn"
                        buttontext={
                            specialniSluzby.seznamTyp === ETypSeznam.SMAZANY
                                ? 'SMAZAT'
                                : specialniSluzby.seznamTyp === ETypSeznam.OVERENY
                                  ? 'OVERIT'
                                  : 'ZAEVIDOVAT'
                        }
                        onClick={() => {
                            if (specialniSluzby.seznamTyp === ETypSeznam.SMAZANY) {
                                confirm({
                                    title: getLanguageObject(language).POTVRDENI,
                                    content: dataZruseni?.polozky.filter(
                                        e =>
                                            e.stav === 'U700' &&
                                            specialniSluzby.detail?.typprevodu === ETypPrevodu.PROTIZAPLACENI
                                    ).length
                                        ? getLanguageObject(language).ZMAZAT_DLT_CONFIRM
                                        : getLanguageObject(language).ZMAZAT_DLT_CONFIRM_SHORT,
                                    cancelText: getLanguageObject(language).NE,
                                    okText: getLanguageObject(language).ANO,
                                    onOk() {
                                        setLoading(true);
                                        ueZruseniDeleteDLT(
                                            specialniSluzby?.detail?.id!,
                                            specialniSluzby.pozadavek
                                                ? {
                                                      idpolozky: specialniSluzby.pozadavek?.idpolozky,
                                                      ucet: specialniSluzby.pozadavek?.ucet,
                                                      zpusobpor: specialniSluzby.pozadavek?.zpusobpor,
                                                      stav: specialniSluzby.pozadavek?.stav
                                                  }
                                                : {
                                                      idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                                                      ucet: specialniSluzby.detailFiltr?.ucet,
                                                      zpusobpor: specialniSluzby.detailFiltr?.zpusobpor,
                                                      stav: specialniSluzby.detailFiltr?.stav
                                                  }
                                        )
                                            .then(resp => {
                                                renderModal(
                                                    resp.chybovystav
                                                        ? `VYMAZANI_${resp.chybovystav}`
                                                        : 'SS_SEZNAM_MODAL_USPESNE_' + specialniSluzby.seznamTyp
                                                );
                                            })
                                            .catch((err: any) => {
                                                renderModal('RESPONSE_ERROR_' + err.response?.status);
                                            })
                                            .finally(() => {
                                                setLoading(false);
                                            });
                                    }
                                });
                            } else {
                                setLoading(true);
                                ueZaevidovaniPostDLT(specialniSluzby?.detail?.id!, {
                                    idpolozky: specialniSluzby.detailFiltr?.idpolozky,
                                    ucet: specialniSluzby.detailFiltr?.ucet,
                                    zpusobpor: specialniSluzby.detailFiltr?.zpusobpor,
                                    stav: specialniSluzby.detailFiltr?.stav
                                })
                                    .then(() => {
                                        renderModal('SS_SEZNAM_MODAL_USPESNE_' + specialniSluzby.seznamTyp);
                                    })
                                    .catch((err: any) => {
                                        // TODO: doplnit stavy pro response uložení podobně jako v NovyRucni.tsx?
                                        renderModal('RESPONSE_ERROR_' + err.response?.status);
                                    })
                                    .finally(() => {
                                        setLoading(false);
                                    });
                            }
                        }}
                    />
                )}
            </Row>
        );
    };

    return (
        <>
            <LoadingWrapper loading={loading}>
                <Title
                    level={4}
                    text={`${getLanguageObject(language).SS_DETAIL_POZADAVEK} ${
                        getLanguageObject(language)['SS_NOVY_POZADAVEK_NA_' + specialniSluzby?.detail?.typ]
                    } - ${getLanguageObject(language).ISIN}: ${specialniSluzby?.detail?.isin}, ${
                        getLanguageObject(language).DATUM
                    }: ${dayjs(specialniSluzby?.detail?.datum).format('DD.MM.YYYY')}`}
                />
                {errStatus ? (
                    <Row>
                        <Col span={16}>
                            {errStatus === 404 ? (
                                <Paragraph text="RESPONSE_ERROR_404" />
                            ) : errStatus ? (
                                <Paragraph text="SLUZBA_NEDOSTUPNA" />
                            ) : null}
                            <Row className="button-row">
                                <ButtonComponent
                                    id="seznamPozadavkuErrBtn"
                                    buttontext="ZPET"
                                    onClick={() => {
                                        setErrStatus(0);
                                    }}
                                />
                            </Row>
                        </Col>
                    </Row>
                ) : (
                    <>
                        {specialniSluzby?.seznamTyp === ETypSeznam.SMAZANY ? (
                            <>
                                <Title level={5} text="SS_NOVY_SOUBOR_SEZNAM_NADPIS_SMAZANI" />
                                <Table
                                    rowKey={(_, index) => index || 0}
                                    columns={columns}
                                    dataSource={dataZruseni?.polozky}
                                    locale={{
                                        emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                                    }}
                                    pagination={{
                                        total: dataZruseni?.count,
                                        // onChange: page => setTableOffset((page - 1) * 10),
                                        // current: tableOffset / 10 + 1,
                                        onChange: page => {
                                            setPagination({
                                                page: page,
                                                offset: (page - 1) * pagination.limit,
                                                limit: pagination.limit
                                            });
                                            loadDataFunc(undefined, (page - 1) * pagination.limit);
                                        },
                                        current: pagination.page,
                                        showSizeChanger: false
                                    }}
                                />
                            </>
                        ) : (
                            <>
                                <Title level={4} text="SS_SEZNAM_ZAEVIDOVAT_NELZE" />
                                <Table
                                    rowKey={(_, index) => index || 0}
                                    columns={columns || []}
                                    dataSource={dataNelze?.polozky || []}
                                    locale={{
                                        emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                                    }}
                                    pagination={{
                                        total: dataNelze?.count,
                                        onChange: page => {
                                            setPaginationNelze({
                                                page: page,
                                                offset: (page - 1) * paginationNelze.limit,
                                                limit: paginationNelze.limit
                                            });

                                            const fn = loadDataFunc(false, (page - 1) * paginationNelze.limit);
                                            if (fn) {
                                                setLoading(true);
                                                fn.then((resp: IZaevidovaniGetDLT) => {
                                                    setDataNelze(resp);
                                                })
                                                    .catch((err: any) => {
                                                        setErrStatus(err.response?.status);
                                                    })
                                                    .finally(() => {
                                                        setLoading(false);
                                                    });
                                            }
                                        },
                                        current: paginationNelze.page,
                                        showSizeChanger: false
                                    }}
                                />
                                {dataNelze?.polozky.length && (
                                    <Row
                                        style={{
                                            paddingTop: '10px'
                                        }}
                                        gutter={8}
                                    >
                                        <Col>
                                            <InputComponent
                                                name="pocetcpnelze"
                                                label="POCET_CP"
                                                notRequired={true}
                                                defaultValue={dataNelze.pocetcp}
                                                disabled={true}
                                            />
                                        </Col>
                                        <Col>
                                            <InputComponent
                                                name="pocetcp"
                                                label="SS_PREHLED_POCET_UCTU"
                                                notRequired={true}
                                                defaultValue={dataNelze.pocetuctu}
                                                disabled={true}
                                            />
                                        </Col>
                                    </Row>
                                )}

                                <Title
                                    style={{
                                        paddingTop: '20px'
                                    }}
                                    level={4}
                                    text="SS_SEZNAM_ZAEVIDOVAT_LZE"
                                />
                                <Table
                                    rowKey={(_, index) => index || 0}
                                    columns={columns || []}
                                    dataSource={dataLze?.polozky || []}
                                    locale={{
                                        emptyText: <TranslateText>TABULKA_ZADNE_ZAZNAMY</TranslateText>
                                    }}
                                    pagination={{
                                        total: dataLze?.count,
                                        onChange: page => {
                                            setPaginationLze({
                                                page: page,
                                                offset: (page - 1) * paginationLze.limit,
                                                limit: paginationLze.limit
                                            });
                                            const fn = loadDataFunc(true, (page - 1) * paginationLze.limit);
                                            if (fn) {
                                                setLoading(true);
                                                fn.then((resp: IZaevidovaniGetDLT) => {
                                                    setDataLze(resp);
                                                })
                                                    .catch((err: any) => {
                                                        setErrStatus(err.response?.status);
                                                    })
                                                    .finally(() => {
                                                        setLoading(false);
                                                    });
                                            }
                                        },
                                        current: paginationLze.page,
                                        showSizeChanger: false
                                    }}
                                />

                                {dataLze?.polozky.length && (
                                    <Row
                                        style={{
                                            paddingTop: '10px'
                                        }}
                                        gutter={8}
                                    >
                                        <Col>
                                            <InputComponent
                                                name="pocetcplze"
                                                label="POCET_CP"
                                                notRequired={true}
                                                defaultValue={dataLze.pocetcp}
                                                disabled={true}
                                            />
                                        </Col>
                                        <Col>
                                            <InputComponent
                                                name="pocetcp"
                                                label="SS_PREHLED_POCET_UCTU"
                                                notRequired={true}
                                                defaultValue={dataLze.pocetuctu}
                                                disabled={true}
                                            />
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                        {renderButtons()}
                    </>
                )}
            </LoadingWrapper>
            <Modal
                title={
                    <TranslateText>
                        {modalMessage.includes('ERROR_')
                            ? 'CHYBA'
                            : specialniSluzby?.seznamTyp === ETypSeznam.SMAZANY
                              ? 'SMAZANI_POZADAVKU'
                              : specialniSluzby?.seznamTyp === ETypSeznam.OVERENY
                                ? 'SS_OVERENI_UCTU'
                                : 'SS_ZAEVIDOVAT_V_CDCP'}
                    </TranslateText>
                }
                open={!!modalMessage}
                onCancel={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') &&
                        navigateWorkaround(
                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT}`
                        );
                }}
                afterClose={() => {
                    setModalMessage('');
                    !modalMessage.includes('ERROR_') &&
                        navigateWorkaround(
                            `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT}`
                        );
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="seznamPozadavkuZpetAltBtn"
                            buttontext="ZAVRIT"
                            type="default"
                            onClick={() => {
                                setModalMessage('');
                                !modalMessage.includes('ERROR_') &&
                                    navigateWorkaround(
                                        `/firma/${aktivniFirma?.iducet}${routePathEnum.SLUZBY_DETAIL_POZADAVKU_DLT}`
                                    );
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <div>
                        {!modalMessage.includes('ERROR_') && (
                            <TranslateText>
                                {specialniSluzby?.seznamTyp === 'SMAZANY'
                                    ? 'SS_SEZNAM_MODAL_USPESNE_POZADAVKY'
                                    : 'SS_SEZNAM_MODAL_USPESNE'}
                            </TranslateText>
                        )}{' '}
                        <TranslateText>{modalMessage}</TranslateText>
                    </div>
                    {!modalMessage.includes('ERROR_') && (
                        <div>
                            <Paragraph text={'SS_SEZNAM_MODAL_ZAEVIDOVAT_TEXT'} />
                        </div>
                    )}
                </Row>
            </Modal>
        </>
    );
};

export default SeznamPozadavkuDLT;
